import { Button, Divider, Group, Paper, Select, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core'
import { useState } from 'react'
import { useBoats } from '@/frontend/hooks/useBoats'
import { useBookings } from '@/frontend/hooks/useBookings'
import { useContracts } from '@/frontend/hooks/useContracts'
import { useCustomers } from '@/frontend/hooks/useCustomers'
import { useInvoices } from '@/frontend/hooks/useInvoices'
import type { CreateAssignmentInput } from '@/lib/marina/validators'
import { RichTextEditor } from './RichTextEditor'

const STATUSES = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'BLOCKED', label: 'Blocked' },
  { value: 'CANCELLED', label: 'Cancelled' },
]
const PRIORITIES = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
]
const CATEGORIES = [
  { value: 'CUSTOMER_SUPPORT', label: 'Customer Support' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'CLEANING', label: 'Cleaning' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'SECURITY', label: 'Security' },
  { value: 'OTHER', label: 'Other' },
]
const REFERENCE_TYPES = [
  { value: 'BOOKING', label: 'Booking' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'BOAT', label: 'Boat' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'OTHER', label: 'Other' },
]

interface Props {
  initialValues?: Partial<CreateAssignmentInput>
  onSubmit: (v: CreateAssignmentInput) => Promise<void> | void
  onCancel: () => void
  submitLabel?: string
  submitting?: boolean
}

export function AssignmentForm({ initialValues, onSubmit, onCancel, submitLabel = 'Save', submitting }: Props) {
  const [title, setTitle] = useState(initialValues?.title ?? '')
  const [description, setDescription] = useState(initialValues?.description ?? '')
  const [status, setStatus] = useState<string | null>(initialValues?.status ?? null)
  const [assignedStaffId, setAssignedStaffId] = useState<string | null>(initialValues?.assignedStaffId ?? null)
  const [startDate, setStartDate] = useState(initialValues?.startDate ?? '')
  const [deadline, setDeadline] = useState(initialValues?.deadline ?? '')
  const [priority, setPriority] = useState<string | null>(initialValues?.priority ?? null)
  const [category, setCategory] = useState<string | null>(initialValues?.category ?? null)
  const [referenceType, setReferenceType] = useState<string | null>(initialValues?.referenceType ?? null)
  const [referenceId, setReferenceId] = useState<string | null>(initialValues?.referenceId ?? null)

  const { data: bookingsData } = useBookings(referenceType === 'BOOKING' ? { pageSize: 50 } : {})
  const { data: customersData } = useCustomers(referenceType === 'CUSTOMER' ? { pageSize: 100 } : {})
  const { data: boatsData } = useBoats(referenceType === 'BOAT' ? { pageSize: 100 } : {})
  const { data: invoicesData } = useInvoices(referenceType === 'INVOICE' ? { pageSize: 50 } : {})
  const { data: contractsData } = useContracts(referenceType === 'CONTRACT' ? { pageSize: 50 } : {})

  const referenceOptions = (() => {
    switch (referenceType) {
      case 'BOOKING':
        return (bookingsData?.items ?? []).map((b) => ({ value: b.id, label: `${b.code} — ${b.boat?.name ?? ''}` }))
      case 'CUSTOMER':
        return (customersData?.items ?? []).map((c) => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))
      case 'BOAT':
        return (boatsData?.items ?? []).map((b) => ({ value: b.id, label: b.name }))
      case 'INVOICE':
        return (invoicesData?.items ?? []).map((i) => ({ value: i.id, label: i.code }))
      case 'CONTRACT':
        return (contractsData?.items ?? []).map((c) => ({ value: c.id, label: c.code }))
      default:
        return []
    }
  })()

  const handleSubmit = () => {
    onSubmit({
      title: title.trim(),
      description: description || null,
      status: (status as any) || null,
      priority: (priority as any) || null,
      category: (category as any) || null,
      startDate: startDate || null,
      deadline: deadline || null,
      assignedStaffId: assignedStaffId || null,
      referenceType: (referenceType as any) || null,
      referenceId: referenceId || null,
    })
  }

  return (
    <Stack gap="lg">
      <Paper p="lg" radius="md">
        <Stack gap="md">
          <TextInput
            label="Assignment"
            placeholder="Add an assignment title"
            required
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select
              label="Status"
              description="Optional"
              data={STATUSES}
              value={status}
              onChange={setStatus}
              clearable
            />
            <TextInput
              label="Assigned Staff (by name/email)"
              description="Optional — staff directory integration in Phase 13"
              value={assignedStaffId ?? ''}
              onChange={(e) => setAssignedStaffId(e.currentTarget.value || null)}
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Start Date"
              type="date"
              description="Optional"
              value={startDate ?? ''}
              onChange={(e) => setStartDate(e.currentTarget.value)}
            />
            <TextInput
              label="Deadline"
              type="date"
              description="Optional"
              value={deadline ?? ''}
              onChange={(e) => setDeadline(e.currentTarget.value)}
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select
              label="Priority"
              description="Optional"
              data={PRIORITIES}
              value={priority}
              onChange={setPriority}
              clearable
            />
            <Select
              label="Category"
              description="Optional"
              data={CATEGORIES}
              value={category}
              onChange={setCategory}
              clearable
            />
          </SimpleGrid>

          <div>
            <Text size="sm" fw={500} mb={4}>
              Description{' '}
              <Text component="span" c="dimmed" size="xs">
                (Optional)
              </Text>
            </Text>
            <RichTextEditor
              value={description ?? ''}
              onChange={setDescription}
              placeholder="Add an assignment description"
            />
          </div>
        </Stack>
      </Paper>

      <Paper p="lg" radius="md">
        <Stack gap={4} mb="md">
          <Title order={5}>What is this assignment related to?</Title>
          <Text size="sm" c="dimmed">
            Optionally link this assignment to a specific booking, customer, or other reference.
          </Text>
        </Stack>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Select
            label="Reference Type"
            description="Optional"
            data={REFERENCE_TYPES}
            value={referenceType}
            onChange={(v) => {
              setReferenceType(v)
              setReferenceId(null)
            }}
            clearable
          />
          <Select
            label="Specific Reference"
            description={!referenceType ? 'Select a reference type first' : 'Optional'}
            placeholder="Select specific reference…"
            data={referenceOptions}
            value={referenceId}
            onChange={setReferenceId}
            disabled={!referenceType || referenceType === 'OTHER'}
            searchable
            clearable
          />
        </SimpleGrid>
      </Paper>

      <Divider />

      <Group justify="flex-end" gap="sm">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="laut" onClick={handleSubmit} loading={submitting} disabled={!title.trim()}>
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  )
}
